import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Chip, CircularProgress, Divider, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { Sparkles, Stethoscope, ClipboardList, CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { safeConsole } from '../../lib/piiProtection';
import { formatDateTime } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

interface LabResult {
  testName: string;
  resultValue: string;
  unit?: string;
  referenceRange?: string;
  status: string;
  interpretation?: string;
}

interface LabAnalysis {
  patientName?: string;
  reportDate?: string;
  summary?: string;
  riskLevel?: string;
  guidance?: string;
  results?: LabResult[];
  cached?: boolean;
  aiModelUsed?: string;
  executionTimeMs?: number;
  analyzedAt?: string;
}

interface PatientLabAnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  labAnalysis: LabAnalysis | null;
}

const AI_GRADIENT = 'linear-gradient(45deg, #7b1fa2 30%, #4a148c 90%)';
const AI_GRADIENT_HOVER = 'linear-gradient(45deg, #6a1b9a 30%, #311b92 90%)';

async function copyToClipboard(text: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      throw new Error('unavailable');
    }
  } catch {
    const el = document.createElement('textarea');
    el.value = text;
    Object.assign(el.style, { position: 'fixed', left: '-999999px', top: '-999999px' });
    document.body.appendChild(el);
    el.focus();
    el.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      safeConsole.error('Fallback copy failed', err);
      throw err;
    } finally {
      document.body.removeChild(el);
    }
  }
}

function buildCopyText(lab: LabAnalysis): string {
  const results =
    lab.results
      ?.map((r) => `- ${r.testName}: ${r.resultValue} ${r.unit || ''} (${r.status || 'Normal'})`)
      .join('\n') || 'No tabular results extracted';
  return [
    'LAB REPORT ANALYSIS',
    '-------------------',
    `Patient: ${lab.patientName || 'Unknown'}`,
    `Date: ${lab.reportDate || 'Not specified'}`,
    '',
    'OVERALL IMPRESSION:',
    lab.summary || 'No summary available',
    '',
    'TEST RESULTS:',
    results,
    '',
    '[AI Generated Interpretation - Please Verify]',
  ].join('\n');
}

export function PatientLabAnalysisDialog({ open, onClose, labAnalysis }: PatientLabAnalysisDialogProps) {
  const { t } = useTranslation('patients');
  const handleCopy = async () => {
    if (!labAnalysis) return;
    try {
      await copyToClipboard(buildCopyText(labAnalysis));
      toast.success('Interpretation copied to clipboard');
    } catch {
      toast.error('Failed to copy. Please select text manually.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'secondary.main', color: 'white' }}>
        <Sparkles size={24} />
        AI Lab Insights: {labAnalysis?.patientName || 'Laboratory Report'}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {labAnalysis ? <AnalysisContent lab={labAnalysis} /> : <LoadingState />}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">{t('dismiss')}</Button>
        <Button
          variant="contained"
          disabled={!labAnalysis}
          onClick={handleCopy}
          sx={{ background: AI_GRADIENT, color: '#fff', '&:hover': { background: AI_GRADIENT_HOVER } }}
        >
          {t('copyInterpretation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LoadingState() {
  const { t } = useTranslation('patients');
  return (
    <Box sx={{ py: 10, textAlign: 'center' }}>
      <CircularProgress color="secondary" />
      <Typography sx={{ mt: 2 }}>{t('analyzingReport')}</Typography>
    </Box>
  );
}

function AnalysisContent({ lab }: { lab: LabAnalysis }) {
  const { t } = useTranslation('patients');
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="secondary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Stethoscope size={20} /> {t('overallImpression')}
        </Typography>
        {lab.cached && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Chip label={t('preAnalyzed')} size="small" sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 'bold' }} />
            <Typography variant="caption" color="text.secondary">{t('retrievedFromRepository')}</Typography>
          </Box>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(123, 31, 162, 0.05)', borderColor: 'secondary.light' }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{lab.summary}</Typography>
        {lab.riskLevel && (
          <Chip
            label={`${lab.riskLevel} RISK`}
            color={lab.riskLevel === 'CRITICAL' || lab.riskLevel === 'HIGH' ? 'error' : lab.riskLevel === 'MODERATE' ? 'warning' : 'success'}
            size="small" sx={{ mt: 1, fontWeight: 700 }}
          />
        )}
      </Paper>

      {lab.guidance && (
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <HelpCircle size={16} /> {t('aiRecommendationForPhysician')}
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', px: 1 }}>{lab.guidance}</Typography>
        </Box>
      )}

      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ClipboardList size={20} /> {t('extractedResults')}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                {['Test Name', 'Value', 'Ref. Range', 'Status', 'Interpretation'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lab.results?.map((entry, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{entry.testName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {entry.resultValue} <Typography component="span" variant="caption">{entry.unit}</Typography>
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{entry.referenceRange}</Typography></TableCell>
                  <TableCell>
                    <Chip
                      label={entry.status} size="small" variant="outlined"
                      color={['HIGH', 'LOW', 'ABNORMAL'].includes(entry.status) ? 'error' : 'success'}
                      icon={entry.status === 'NORMAL' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                    />
                  </TableCell>
                  <TableCell><Typography variant="caption">{entry.interpretation}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" align="center">
          {t('aiExtractionCanContainErrorsPlease')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
          Model: {lab.aiModelUsed} | Time: {lab.executionTimeMs}ms
          {lab.analyzedAt ? ` | Analyzed: ${formatDateTime(lab.analyzedAt)}` : ''}
        </Typography>
      </Box>
    </Stack>
  );
}
